import type { signalFireSection } from '../../../../app/_shared/marketing/landingContent';
import { AppScreenshot } from './AppScreenshot';
import {
  SIGNAL_FIRE_WHY_SCREENSHOT,
  SIGNAL_FIRE_SHARED_GROUND_SCREENSHOT,
  SIGNAL_FIRE_REVEAL_SCREENSHOT,
} from '../../assets/marketing/app-screenshots/appScreenshotsExport';
import './landingRef.css';

type Props = Pick<
  typeof signalFireSection,
  'eyebrow' | 'h2' | 'lead' | 'steps' | 'footnote' | 'chips'
>;

/**
 * Signal Fire — the daily 1:1 lane, sold as a peer of Circles rather than a Circle add-on.
 * Copy lives in `landingContent.signalFireSection` so the app and the site stay in step.
 */
export function SignalFireSection({ eyebrow, h2, lead, steps, footnote, chips }: Props) {
  const headingLines = h2.split('\n');

  return (
    <section className="signal-fire-section" aria-labelledby="signal-fire-heading">
      <header className="signal-fire-section__intro">
        <p className="section-eyebrow ml-text-eyebrow">{eyebrow}</p>
        {/* h3, not h2 — this lane sits under "The Circle is only the start". */}
        <h3 id="signal-fire-heading" className="ml-heading-serif ml-text-heading">
          {headingLines.map((line, i) => (
            <span key={line} className={i > 0 ? 'signal-fire-section__headingAccent' : undefined}>
              {line}
              {i < headingLines.length - 1 ? <br /> : null}
            </span>
          ))}
        </h3>
        <p className="signal-fire-section__lead ml-text-body-secondary">{lead}</p>
      </header>

      <div className="signal-fire-section__grid">
        {/*
          Real captures, not the hand-drawn `SignalFirePreview` this used to render. This section
          carries the site's headline claim, and it was the one place with no actual app UI in it —
          a real screenshot outperforms a stylised mock even when the mock is prettier.
          One crop per step, in the steps' own order: who you were given, the ground you share,
          then the reveal — which leads, because it is the claim the whole page rests on. Not
          `priority`: this section sits thousands of pixels below the fold, so eager high-priority
          fetches here only competed with the hero image for bandwidth.
        */}
        <div className="signal-fire-section__visual signal-fire-section__visual--shots">
          <AppScreenshot shot={SIGNAL_FIRE_WHY_SCREENSHOT} treatment="moment" width="dense" />
          <AppScreenshot
            shot={SIGNAL_FIRE_SHARED_GROUND_SCREENSHOT}
            treatment="moment"
            width="dense"
          />
          <AppScreenshot shot={SIGNAL_FIRE_REVEAL_SCREENSHOT} treatment="moment" width="lead" />
        </div>

        <ol className="signal-fire-section__steps">
          {steps.map((step, i) => (
            <li key={step.id} className="signal-fire-section__step">
              <span className="signal-fire-section__stepIndex" aria-hidden>
                {i + 1}
              </span>
              <div className="signal-fire-section__stepBody">
                <h4 className="ml-heading-sans ml-text-heading">{step.label}</h4>
                <p className="ml-text-body">{step.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>

      <footer className="signal-fire-section__footer">
        <ul className="signal-fire-section__chips">
          {chips.map((chip) => (
            <li key={chip} className="signal-fire-section__chip">
              {chip}
            </li>
          ))}
        </ul>
        <p className="signal-fire-section__footnote">{footnote}</p>
      </footer>
    </section>
  );
}
